import { useGetConsultations } from "apollo/hooks/queries";
import { Result, isLoading } from "apollo/utils";
import RootPage from "containers/RootPage";
import { useSenderAccountContext } from "context/SenderAccountContext";
import {
  ACCOUNT_ROLE_CARESEEKER_USER_MANAGEMENT,
  ACCOUNT_ROLE_CLINICAL_DOCUMENTATION,
  ACCOUNT_ROLE_DTA_APPROVER,
  ACCOUNT_ROLE_PREDICT,
  ACCOUNT_ROLE_REPORT,
  ACCOUNT_ROLE_SENDER_IT,
  CREATE_PATIENT_ROLES,
  PATIENT_TAB_FILES,
  PATIENT_TAB_MAP,
  PATIENT_TAB_PROFILE,
  PATIENT_TAB_REHAB_FORMS,
  PATIENT_TAB_SEARCH,
  PATIENT_TAB_TIMELINE,
  PATIENT_TAB_TRANSITIONAL_CARE,
  REDIRECT_PARAM,
  REHAB_FORM_APPROVAL_PAGE,
  TRACK_EVENTS,
} from "core/consts";
import {
  TOKEN_STATUS,
  checkTokenStatus,
  hasAccessToDoctorDashboard,
  useHasAccessToAiChat,
} from "core/model/accounts";
import { ExtractDocsProvider } from "context/ExtractDocsContext";
import { isFilesActivatedAtAuctionLevel } from "core/model/auctions";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import SpinnerPage, {
  SpinnerPageContainer,
} from "ds_legacy/components/Spinner";
import { UnsupportedBrowser } from "dsl/atoms/ErrorPages";
import RedirectRoot from "dsl/atoms/RedirectRoot";
import { SearchCandidatesProvider } from "dsl/atoms/SearchCandidates";
import ThrowError from "dsl/atoms/ThrowError";
import lazy from "dsl/atoms/retry";
import { CareseekerAuthPage } from "dsl/ecosystems/CareseekerAuthPage";
import ConsultationLayout, {
  useConsultationsContext,
} from "dsl/ecosystems/Consultation/ConsultationLayout";
import { OnPremiseSetupConfigProvider } from "dsl/ecosystems/OnPremiseDebugPage/OnPremiseSetupConfigContext";
import { areNewRehabFormsActive } from "dsl/ecosystems/PatientForms/RehabForms/utils";
import { getTransitionalCareAction } from "dsl/ecosystems/PatientForms/TransitionalCare";
import { RecareDocsProvider } from "dsl/ecosystems/RecareDocs/context";
import { PatientAssessmentMenu } from "dsl/organisms/PatientAssessmentMenu";
import { PatientAuctionMenu } from "dsl/organisms/PatientAuctionMenu";
import PatientConsultationMenu from "dsl/organisms/PatientConsultationMenu";
import { PatientContentLayout } from "dsl/organisms/PatientContentLayout";
import { DebugPage, RequireCondition } from "entries/receiver/Router/helpers";
import { RequireRoles } from "entries/sender/Router/RequireRoles";
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  Suspense,
  useEffect,
  useState,
} from "react";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useTracking } from "react-tracking";
import {
  useAppDispatch,
  useAppSelector,
  useIsLoggedOnClinicApp,
  useLoggedCareseeker,
} from "reduxentities/hooks";
import { selectCareseekers, selectCredentials } from "reduxentities/selectors";
import { CARESEEKER_CHANGED, LOGGED_OUT } from "reduxentities/slices/authSlice";
import PatientRouteLayout, {
  PatientAuctionOutlet,
  PatientProfileIndex,
  usePatientContext,
} from "./PatientRouteLayout";
import {
  defaultRedirectPath,
  doctorDashboardRedirectPath,
  onpremDebugRedirectPath,
} from "./paths";
import { getRedirectPathBasedOnRoles } from "./utils";

const RehabFormApprovalPage = lazy(
  () => import("dsl/ecosystems/RehabFormApprovalPage"),
);
const ForgotPasswordPage = lazy(
  () => import("dsl/ecosystems/ForgotPasswordPage"),
);
const OnPremiseSetupImportsPage = lazy(
  () => import("dsl/ecosystems/OnPremiseImportsPage"),
);
const OnPremiseSetupInstallPage = lazy(
  () => import("dsl/ecosystems/OnPremiseInstallPage"),
);
const OnPremiseSetupLayout = lazy(
  () => import("dsl/ecosystems/OnPremiseDebugPage/OnPremiseSetupLayout"),
);
const OnPremiseSetupLogsPage = lazy(
  () => import("dsl/ecosystems/OnPremiseLogsPage"),
);
const OnPremiseSetupTestPage = lazy(
  () => import("dsl/ecosystems/OnPremiseTestPage"),
);
const OnPremiseDTATestRoutePage = lazy(
  () => import("dsl/ecosystems/OnPremiseDTATestRoutePage"),
);

const SenderDashboardPage = lazy(
  () => import("dsl/ecosystems/SenderDashboard"),
);
const DoctorDashboard = lazy(() => import("dsl/ecosystems/DoctorDashboard"));
const ReportPage = lazy(
  () => import("dsl/organisms/Careseeker/ClinicReportPage"),
);
const UserManagementPage = lazy(
  () => import("dsl/ecosystems/UserManagementPage"),
);
const PredictPage = lazy(() => import("dsl/ecosystems/PredictPage"));
const PatientDetailsPage = lazy(
  () => import("dsl/ecosystems/PredictDetailsPage"),
);
const PredictStationGuard = lazy(
  () => import("dsl/ecosystems/PredictDetailsPage/PredictStationGuard"),
);
const DiagnosticPage = lazy(() => import("dsl/organisms/DiagnosticPage"));
const LogoutPage = lazy(() => import("dsl/ecosystems/LogoutPage"));
const PrintableSenderDashboardPage = lazy(
  () => import("dsl/ecosystems/SenderDashboard/PrintableSenderDashboard"),
);
const PatientCreatePage = lazy(
  () => import("dsl/ecosystems/PatientCreatePage"),
);
const PasswordResetPageV2 = lazy(
  () => import("dsl/organisms/PasswordResetPageV2"),
);
const PasswordChangePageV2 = lazy(
  () => import("dsl/ecosystems/PasswordChangePageV2"),
);
const ClinicDashboard = lazy(() => import("dsl/ecosystems/ClinicDashboard"));
const ConsultationFilesPage = lazy(
  () => import("dsl/ecosystems/Consultation/ConsultationFiles"),
);
const AgUiChatPage = lazy(() => import("dsl/ecosystems/AiChat/AgUiChatPage"));
const PatientSearch = lazy(() => import("dsl/ecosystems/PatientSearchMerge"));
const PatientProfile = lazy(() => import("dsl/ecosystems/PatientProfile"));
const PatientFiles = lazy(() => import("dsl/ecosystems/PatientFiles"));
const PatientTimeline = lazy(() => import("dsl/ecosystems/AuctionTimeline"));
const PatientMap = lazy(() => import("dsl/ecosystems/PatientMap"));
const PatientTransitionalCare = lazy(
  () => import("dsl/ecosystems/PatientForms/TransitionalCare"),
);
const PatientRehabFormsHome = lazy(
  () => import("dsl/ecosystems/PatientForms/RehabForms"),
);
const RehabFormRenderer = lazy(
  () => import("dsl/ecosystems/PatientForms/RehabForms/FormRenderer"),
);
const PatientAssessment = lazy(
  () => import("dsl/ecosystems/PatientAssessment"),
);
const BarthelSummaryPage = lazy(
  () =>
    import("dsl/ecosystems/IndependenceScaleSummaryPage/BarthelSummaryPage"),
);
const SingerSummaryPage = lazy(
  () => import("dsl/ecosystems/IndependenceScaleSummaryPage/SingerSummaryPage"),
);
const EarlyRehabSummaryPage = lazy(
  () =>
    import("dsl/ecosystems/IndependenceScaleSummaryPage/EarlyRehabSummaryPage"),
);
const AuctionResponse = lazy(
  () => import("dsl/ecosystems/AuctionResponse/connect"),
);
const DischargeConfirmation = lazy(
  () => import("dsl/ecosystems/DischargeConfirmation"),
);
const PrintableSearchMergePanel = lazy(
  () => import("dsl/ecosystems/PatientSearchMerge/print"),
);

const EmptyPage = () => <div style={{ flex: "1" }} />;

function RequireAiChatAccess({
  children,
  redirectTo,
}: {
  children?: React.JSX.Element | null;
  redirectTo: string;
}) {
  const hasAccess = useHasAccessToAiChat();
  return (
    <RequireCondition hasCondition={hasAccess} redirectTo={redirectTo}>
      {children}
    </RequireCondition>
  );
}

/**
 * Route component for auction/:auctionId/*. Validates auction belongs to patient,
 * then loads auction and provides outlet for PatientAuctionRouteLayout.
 */
function PatientAuctionsRoute() {
  const { patient } = usePatientContext();
  const { auctionId, patientId } = useParams<{
    auctionId: string;
    patientId: string;
  }>();
  if (!patient || !auctionId) return null;

  const auctionFromPatient = patient.auctions?.find(
    (a) => a.id === Number(auctionId),
  );
  if (!auctionFromPatient) {
    return (
      <Navigate replace to={`/app/clinic/dashboard/patient/${patientId}`} />
    );
  }

  return (
    <PatientAuctionOutlet auctionId={Number(auctionId)} patient={patient} />
  );
}

/**
 * Layout for patient/:patientId/auction/:auctionId/* routes.
 * Uses patient + auction from PatientRoute outlet context.
 */
function PatientAuctionRouteLayout() {
  const { auction, patient } = usePatientContext();
  const params = useParams();
  const { isRedesignActive: isDocsRedesignActive } =
    useIsDocsAndExtractActive();

  const splat = params["*"];
  const hasNoTabSegment = splat === "" || splat === undefined;
  if (hasNoTabSegment) {
    return <Navigate replace to={PATIENT_TAB_SEARCH} />;
  }

  if (!auction) {
    return null;
  }

  const activateRehabFormsFeature = areNewRehabFormsActive(auction);

  return (
    <Routes>
      <Route element={<SearchCandidatesProvider />}>
        <Route
          element={
            <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
              <PatientAssessmentMenu auction={auction} patient={patient} />
            </RequireRoles>
          }
          path="assessing/*"
        >
          <Route
            element={
              <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                <PatientAssessment auction={auction} />
              </RequireRoles>
            }
            index
          />
          <Route
            element={
              <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                <PatientAssessment auction={auction} />
              </RequireRoles>
            }
            path=":assessmentSlug"
          />
        </Route>
        <Route
          element={
            <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
              <BarthelSummaryPage auctionId={auction.id} patient={patient} />
            </RequireRoles>
          }
          path="barthel-index"
        />
        <Route
          element={
            <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
              <SingerSummaryPage auctionId={auction.id} patient={patient} />
            </RequireRoles>
          }
          path="singer-index"
        />
        <Route
          element={
            <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
              <EarlyRehabSummaryPage auctionId={auction.id} patient={patient} />
            </RequireRoles>
          }
          path="early-rehab-index"
        />
        <Route
          element={
            <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
              <AuctionResponse />
            </RequireRoles>
          }
          path="request/:requestId"
        />
        <Route
          element={
            <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
              <ExtractDocsProvider auction={auction} patient={patient}>
                <RecareDocsProvider auction={auction} patient={patient}>
                  <PatientAuctionMenu auction={auction} patient={patient} />
                </RecareDocsProvider>
              </ExtractDocsProvider>
            </RequireRoles>
          }
          path="*"
        >
          <Route element={<Navigate replace to={PATIENT_TAB_SEARCH} />} index />
          <Route
            element={
              <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                <PatientSearch auction={auction} patient={patient} />
              </RequireRoles>
            }
            path={PATIENT_TAB_SEARCH}
          />
          <Route
            element={
              <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                <PatientProfile auction={auction} />
              </RequireRoles>
            }
            path={PATIENT_TAB_PROFILE}
          />
          <Route
            element={
              <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                <RequireCondition
                  hasCondition={isFilesActivatedAtAuctionLevel(auction)}
                  redirectTo={PATIENT_TAB_SEARCH}
                />
              </RequireRoles>
            }
          >
            <Route
              element={
                <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                  <PatientFiles
                    auction={auction}
                    consultation={undefined}
                    patient={patient}
                  />
                </RequireRoles>
              }
              path={PATIENT_TAB_FILES}
            />
            <Route
              element={
                <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                  <PatientRehabFormsHome auction={auction} patient={patient} />
                </RequireRoles>
              }
              path={`${PATIENT_TAB_FILES}/${PATIENT_TAB_REHAB_FORMS}`}
            />
          </Route>
          <Route
            element={
              <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                <PatientTimeline auction={auction} patient={patient} />
              </RequireRoles>
            }
            path={PATIENT_TAB_TIMELINE}
          />
          <Route
            element={
              <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                <RequireCondition
                  hasCondition={
                    !!getTransitionalCareAction(auction) &&
                    !isDocsRedesignActive
                  }
                  redirectTo={PATIENT_TAB_SEARCH}
                />
              </RequireRoles>
            }
          >
            <Route
              element={
                <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                  <PatientTransitionalCare
                    auction={auction}
                    patient={patient}
                  />
                </RequireRoles>
              }
              path={PATIENT_TAB_TRANSITIONAL_CARE}
            />
          </Route>
          <Route
            element={
              <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                <PatientMap auction={auction} />
              </RequireRoles>
            }
            path={PATIENT_TAB_MAP}
          />
          <Route
            element={
              <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                <RequireCondition
                  hasCondition={
                    activateRehabFormsFeature && !isDocsRedesignActive
                  }
                  redirectTo={PATIENT_TAB_SEARCH}
                />
              </RequireRoles>
            }
          >
            <Route
              element={
                <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                  <PatientRehabFormsHome auction={auction} patient={patient} />
                </RequireRoles>
              }
              path={PATIENT_TAB_REHAB_FORMS}
            />
            <Route
              element={
                <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                  <RehabFormRenderer auction={auction} patient={patient} />
                </RequireRoles>
              }
              path={`${PATIENT_TAB_REHAB_FORMS}/:form`}
            />
          </Route>
          <Route
            element={<Navigate replace to={PATIENT_TAB_SEARCH} />}
            path="*"
          />
        </Route>
      </Route>
      <Route element={<DischargeConfirmation />} path="discharge" />
      <Route
        element={<PrintableSearchMergePanel />}
        path="printable-search-panel"
      />
    </Routes>
  );
}

function DashboardIndexOrRedirect() {
  const { activeRoles } = useSenderAccountContext();
  const hasDoctorDashboardOnly = hasAccessToDoctorDashboard(activeRoles);

  if (hasDoctorDashboardOnly) {
    return (
      <Suspense fallback={<SpinnerPage id="doctor-dashboard" />}>
        <DoctorDashboard />
      </Suspense>
    );
  }
  return <Navigate replace to={defaultRedirectPath} />;
}

function PatientIndexRedirect() {
  const { patient } = usePatientContext();
  const { patientId } = useParams<{ patientId: string }>();
  const [progress, clinicalConsultations] = useGetConsultations({
    patientId: patient?.id,
  });

  return (
    <Result
      data={clinicalConsultations}
      id="patient-index-redirect"
      queryProgress={progress}
    >
      {(consultations) => {
        const firstConsultation = consultations?.[0];
        if (firstConsultation) {
          return (
            <Navigate
              replace
              to={`/app/clinic/dashboard/patient/${patientId}/consultation/${firstConsultation.id}/files`}
            />
          );
        }
        return <Navigate replace to={doctorDashboardRedirectPath} />;
      }}
    </Result>
  );
}

function PatientIndexOrProfile() {
  const { activeRoles } = useSenderAccountContext();
  const hasDoctorDashboardOnly = hasAccessToDoctorDashboard(activeRoles);

  if (hasDoctorDashboardOnly) {
    return (
      <Suspense fallback={<SpinnerPage id="patient-index-redirect" />}>
        <PatientIndexRedirect />
      </Suspense>
    );
  }
  return <PatientProfileIndex />;
}

function CheckRoles({
  children,
  defaultRoute,
  setDefaultRoute,
}: {
  children?: ReactNode;
  defaultRoute: string;
  setDefaultRoute: Dispatch<SetStateAction<string>>;
}) {
  const { account, activeRoles } = useSenderAccountContext();

  useEffect(() => {
    const desiredPath = getRedirectPathBasedOnRoles({ account, activeRoles });
    if (defaultRoute !== desiredPath) {
      setDefaultRoute(desiredPath);
    }
  }, [defaultRoute, account, activeRoles, setDefaultRoute]);

  return <>{children ?? <Outlet />}</>;
}

function CareseekerAuthCheck({ children }: { children?: ReactNode | null }) {
  const { trackEvent } = useTracking();
  const dispatch = useAppDispatch();
  const logged = useIsLoggedOnClinicApp();
  const credentials = useAppSelector(selectCredentials);

  useEffect(() => {
    const tokenStatus = checkTokenStatus(credentials);
    if (tokenStatus === TOKEN_STATUS.EXPIRED) {
      trackEvent({ name: TRACK_EVENTS.ROUTE_SESSION_EXPIRED });
      dispatch(LOGGED_OUT());
    }
  }, [credentials, dispatch, trackEvent]);

  // If not logged, redirect to authentication page.
  if (logged !== "ok") {
    return (
      <Navigate
        to={`/auth?${REDIRECT_PARAM}=${encodeURIComponent(
          `${location.pathname}${location.search}`,
        )}`}
      />
    );
  }

  // Otherwise, render children or the default outlet.
  return <>{children ?? <Outlet />}</>;
}

export function CareseekerRedirector() {
  const { careseekerId } = useParams();
  const loggedCareseekerId = useLoggedCareseeker()?.id;
  const careseekers = useAppSelector(selectCareseekers);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!careseekerId) {
      return;
    }

    // Wait until careseeker list from identification is available so we can
    // sync Redux (active_roles) before rewriting the URL. Otherwise we can land
    // on /dashboard with a stale careseeker and wrong menu permissions.
    if (careseekers === undefined) {
      return;
    }

    const selectedCareseeker = careseekers.find(
      ({ id }) => id === Number(careseekerId),
    );
    if (
      selectedCareseeker?.id &&
      selectedCareseeker.id !== loggedCareseekerId
    ) {
      dispatch(
        CARESEEKER_CHANGED({
          careseeker_id: selectedCareseeker.id,
          careseeker_name: selectedCareseeker?.name,
          country: selectedCareseeker?.address?.country,
        }),
      );
    }

    // Replace the first segment (careseekerId) with "dashboard"
    const updatedPath = location.pathname.replace(
      `/${careseekerId}`,
      "/dashboard",
    );

    navigate(updatedPath + location.search + location.hash, {
      replace: true,
    });
  }, [
    careseekerId,
    careseekers,
    dispatch,
    location.hash,
    location.pathname,
    location.search,
    loggedCareseekerId,
    navigate,
  ]);

  return (
    <SpinnerPageContainer>
      <SpinnerPage id="careseeker-redirector" />
    </SpinnerPageContainer>
  );
}

function ConsultationIndexRedirect() {
  const { clinicalConsultations, clinicalConsultationsProgress } =
    useConsultationsContext();
  const { patientId } = useParams<{ patientId: string }>();

  if (isLoading(clinicalConsultationsProgress)) {
    return <SpinnerPage id="consultation-index-redirect" />;
  }

  const firstConsultation = clinicalConsultations[0];
  if (firstConsultation != null && patientId != null) {
    return (
      <Navigate
        replace
        to={`/app/clinic/dashboard/patient/${patientId}/consultation/${firstConsultation.id}/files`}
      />
    );
  }

  return <Navigate replace to={doctorDashboardRedirectPath} />;
}

function ClinicalConsultationsRoute() {
  const { patient } = usePatientContext();
  const [clinicalConsultationsProgress, clinicalConsultations] =
    useGetConsultations({
      patientId: patient?.id,
    });

  return (
    <>
      <PatientConsultationMenu patient={patient} />
      <PatientContentLayout variant="menu">
        <Result
          data={clinicalConsultations}
          id="clinical-consultation-routes"
          queryProgress={clinicalConsultationsProgress}
        >
          {(consultations) => (
            <Outlet
              context={{
                clinicalConsultations: consultations,
                clinicalConsultationsProgress,
                patient,
              }}
            />
          )}
        </Result>
      </PatientContentLayout>
    </>
  );
}

export default function CareseekerRoutes() {
  // Start at dashboard index so DashboardIndexOrRedirect can send doctor-only users
  // to the doctor dashboard without briefly hitting todo/active (which would show access denied).
  const [defaultRoute, setDefaultRoute] = useState(doctorDashboardRedirectPath);

  return (
    <Routes>
      <Route element={<RedirectRoot to={defaultRoute} />} index />
      <Route element={<RootPage appName="clinic" />} path="/">
        <Route element={<CareseekerAuthPage />} path="auth" />
        <Route element={<CareseekerAuthPage />} path="auth/:token" />
        <Route element={<DiagnosticPage />} path="diagnostic" />
        <Route element={<ForgotPasswordPage />} path="forgot-password" />
        <Route element={<EmptyPage />} path="home" />
        <Route element={<DebugPage />} path="info" />
        <Route element={<LogoutPage />} path="logout" />
        <Route element={<ThrowError />} path="throwerror" />
        <Route element={<UnsupportedBrowser />} path="unsupported_browser" />
        <Route path="app/*">
          <Route element={<CareseekerAuthCheck />} path="clinic/*">
            <Route path=":careseekerId/*">
              <Route
                element={<PrintableSenderDashboardPage />}
                path="dashboard-print"
              />
              <Route element={<CareseekerRedirector />} path="*" />
            </Route>
            <Route path="dashboard/*">
              <Route element={<ClinicDashboard />}>
                <Route
                  element={
                    <CheckRoles
                      defaultRoute={defaultRoute}
                      setDefaultRoute={setDefaultRoute}
                    />
                  }
                >
                  <Route element={<DashboardIndexOrRedirect />} index />
                  <Route path="todo">
                    <Route
                      element={
                        <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                          <SenderDashboardPage />
                        </RequireRoles>
                      }
                      path=":tab"
                    />
                    <Route
                      element={<Navigate replace to={defaultRoute} />}
                      path="*"
                    />
                  </Route>
                  <Route
                    element={<Navigate replace to={onpremDebugRedirectPath} />}
                    path="debug-on-premise"
                  />
                  <Route
                    element={
                      <RequireRoles requiredRoles={[ACCOUNT_ROLE_SENDER_IT]}>
                        <OnPremiseSetupConfigProvider>
                          <Suspense
                            fallback={<SpinnerPage id="onprem-setup-layout" />}
                          >
                            <OnPremiseSetupLayout />
                          </Suspense>
                        </OnPremiseSetupConfigProvider>
                      </RequireRoles>
                    }
                    path="onpremsetup"
                  >
                    <Route element={<Navigate replace to="test" />} index />
                    <Route element={<OnPremiseSetupTestPage />} path="test" />
                    <Route
                      element={<OnPremiseDTATestRoutePage />}
                      path="dtatest"
                    />
                    <Route
                      element={<OnPremiseSetupInstallPage />}
                      path="install"
                    />
                    <Route
                      element={<OnPremiseSetupImportsPage />}
                      path="imports"
                    />
                    <Route element={<OnPremiseSetupLogsPage />} path="logs" />
                    <Route element={<Navigate replace to="test" />} path="*" />
                  </Route>
                  <Route
                    element={
                      <RequireRoles requiredRoles={[ACCOUNT_ROLE_DTA_APPROVER]}>
                        <RehabFormApprovalPage />
                      </RequireRoles>
                    }
                    path={REHAB_FORM_APPROVAL_PAGE}
                  />
                  <Route
                    element={
                      <RequireRoles
                        requiredRoles={[
                          ACCOUNT_ROLE_CARESEEKER_USER_MANAGEMENT,
                        ]}
                      >
                        <UserManagementPage />
                      </RequireRoles>
                    }
                    path="users"
                  />
                  <Route
                    element={
                      <RequireRoles requiredRoles={[ACCOUNT_ROLE_REPORT]}>
                        <ReportPage />
                      </RequireRoles>
                    }
                    path="report"
                  />
                  <Route
                    element={
                      <RequireRoles requiredRoles={[ACCOUNT_ROLE_PREDICT]} />
                    }
                    path="predict"
                  >
                    <Route element={<PredictPage />} index />
                    <Route element={<PredictStationGuard />} path=":patientId">
                      <Route element={<PatientDetailsPage />} index />
                    </Route>
                  </Route>
                  <Route
                    element={<RequireAiChatAccess redirectTo={defaultRoute} />}
                    path="chat"
                  >
                    <Route element={<AgUiChatPage />} index />
                    <Route element={<AgUiChatPage />} path=":chatId" />
                  </Route>
                  <Route
                    element={
                      <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                        <ReportPage isTestQuickSightDashboard />
                      </RequireRoles>
                    }
                    path="report_test"
                  />
                  <Route
                    element={
                      <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                        <PatientCreatePage />
                      </RequireRoles>
                    }
                    path="patient/new"
                  />
                  <Route
                    element={
                      <Navigate replace to={doctorDashboardRedirectPath} />
                    }
                    path="patient"
                  />
                  <Route
                    element={
                      <RequireRoles
                        requiredRoles={[
                          ...CREATE_PATIENT_ROLES,
                          ACCOUNT_ROLE_CLINICAL_DOCUMENTATION,
                        ]}
                      >
                        <PatientRouteLayout />
                      </RequireRoles>
                    }
                    path="patient/:patientId"
                  >
                    <Route element={<PatientIndexOrProfile />} index />
                    <Route
                      element={<ClinicalConsultationsRoute />}
                      path="consultation/*"
                    >
                      <Route element={<ConsultationIndexRedirect />} index />
                      <Route
                        element={<ConsultationLayout />}
                        path=":consultationId"
                      >
                        <Route
                          element={<Navigate replace to="files" />}
                          index
                        />
                        <Route
                          element={
                            <Suspense
                              fallback={
                                <SpinnerPage id="consultation-files-page" />
                              }
                            >
                              <ConsultationFilesPage />
                            </Suspense>
                          }
                          path="files"
                        />
                      </Route>
                    </Route>
                    <Route
                      element={
                        <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                          <PatientAuctionsRoute />
                        </RequireRoles>
                      }
                      path="auction/:auctionId/*"
                    >
                      <Route element={<PatientAuctionRouteLayout />} path="*" />
                    </Route>
                  </Route>
                  <Route
                    element={<Navigate replace to={defaultRoute} />}
                    path="*"
                  />
                  <Route
                    element={
                      <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                        <PasswordChangePageV2 />
                      </RequireRoles>
                    }
                    path="my-password-change"
                  />
                  <Route
                    element={<Navigate replace to={defaultRoute} />}
                    path="*"
                  />
                </Route>
              </Route>
              <Route
                element={<Navigate replace to={defaultRoute} />}
                path="*"
              />
            </Route>

            <Route element={<Navigate replace to={defaultRoute} />} path="*" />
          </Route>
          <Route element={<Navigate replace to={defaultRoute} />} path="*" />
        </Route>
        <Route
          element={<PasswordResetPageV2 authRoute="/app/auth" />}
          path="reset/:token"
        />
        <Route element={<RedirectRoot to={defaultRoute} />} path="*" />
      </Route>
    </Routes>
  );
}
